import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, Switch, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useMicrosoftIntegration } from '../../hooks/useMicrosoftIntegration';
import { useConnectorList } from '../../hooks/useConnectorList';
import MicrosoftIntegrationSection from './MicrosoftIntegrationSection';
import ConnectorSetupModal from './ConnectorSetupModal';

const IntegrationsManager = () => {
  const {
    msStatus, msDisconnecting, handleMsConnect, handleMsDisconnect,
  } = useMicrosoftIntegration();

  const {
    userConnectorAuth,
    currentConnector, connectorInputs, setConnectorInputs,
    isConnectorModalOpen, setIsConnectorModalOpen,
    isConnectorLoading, modalError, setModalError,
    confirmationAction, setConfirmationAction,
    groupedConnectors,
    openSetupModal, handleToggle, handleConfirmToggle, handleAuthorizeConnector, handleOAuthDisconnect,
  } = useConnectorList();

  return (
    <Box>
      <MicrosoftIntegrationSection
        msStatus={msStatus} msDisconnecting={msDisconnecting}
        onConnect={handleMsConnect} onDisconnect={handleMsDisconnect}
      />

      {Object.keys(groupedConnectors)
        .sort((a, b) => groupedConnectors[b].length - groupedConnectors[a].length)
        .map((category) => (
          <Box key={category} mb={4}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
              sx={{ mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.08em' }}>
              {category}
            </Typography>
            <Divider sx={{ mb: 2.5 }} />
            <Grid container spacing={2.5}>
              {groupedConnectors[category].map((conn) => {
                const isActive = conn.isConfigured && conn.status;
                const isOAuth = conn.authType === 'oauth';
                return (
                  <Grid item key={conn.id} xs={12} sm={6} md={4} lg={3}>
                    <Card sx={{
                      borderRadius: '12px', border: '1.5px solid', borderColor: '#E5E7EB',
                      boxShadow: 'none', height: '100%', display: 'flex', flexDirection: 'column',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)', borderColor: 'primary.main' },
                    }}>
                      <Box sx={{ background: conn.bgColor, p: 2.5, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80, borderRadius: '10px 10px 0 0' }}>
                        {typeof conn.icon === 'string' ? (
                          conn.icon.length === 1
                            ? <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{conn.icon}</Typography>
                            : <img src={conn.icon} alt={conn.name} style={{ width: 36, height: 36, objectFit: 'contain' }} />
                        ) : null}
                      </Box>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
                          <Typography fontWeight={600} fontSize="0.9375rem" color="text.primary">{conn.name}</Typography>
                          {isActive ? (
                            <Chip icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />} label="Connected" size="small"
                              sx={{ fontSize: '0.6875rem', height: 22, bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 600, '& .MuiChip-icon': { color: '#059669' } }} />
                          ) : conn.isConfigured ? (
                            <Chip label="Disabled" size="small"
                              sx={{ fontSize: '0.6875rem', height: 22, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 500 }} />
                          ) : (
                            <Chip icon={<RadioButtonUncheckedIcon sx={{ fontSize: '14px !important' }} />} label="Not connected" size="small"
                              sx={{ fontSize: '0.6875rem', height: 22, bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 500 }} />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, lineHeight: 1.6, mb: 1.5, fontSize: '0.8125rem' }}>
                          {conn.description}
                        </Typography>
                        <Box display="flex" gap={0.75} flexWrap="wrap" mb={1.5}>
                          {conn.capabilities?.map((cap) => (
                            <Chip key={cap} label={cap} size="small"
                              sx={{ fontSize: '0.625rem', height: 18, bgcolor: '#EFF6FF', color: '#1D4ED8', fontWeight: 600, borderRadius: '4px' }} />
                          ))}
                        </Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt="auto">
                          {conn.isConfigured ? (
                            <>
                              {isOAuth ? (
                                <Button size="small" variant="outlined" color="error" onClick={() => handleOAuthDisconnect(conn)} sx={{ fontSize: '0.8125rem', px: 1.5 }}>
                                  Disconnect
                                </Button>
                              ) : (
                                <Button size="small" variant="contained" onClick={() => openSetupModal(conn)} sx={{ fontSize: '0.8125rem', px: 1.5 }}>
                                  Manage
                                </Button>
                              )}
                              {!isOAuth && <Switch checked={conn.status} onChange={(e) => handleToggle(e, conn)} onClick={(e) => e.stopPropagation()} size="small" />}
                            </>
                          ) : (
                            <Button size="small" variant="contained" fullWidth
                              onClick={() => isOAuth ? (() => {
                                const token = localStorage.getItem('token');
                                if (token) {
                                  const returnTo = (window.location.pathname.includes('/organisations') || window.location.pathname.includes('/admin')) ? 'organisations' : 'profile';
                                  window.location.href = `/api/connector/auth/${conn.id}?token=${token}&returnTo=${returnTo}`;
                                }
                              })() : openSetupModal(conn)}
                              endIcon={isOAuth ? <OpenInNewIcon sx={{ fontSize: 14 }} /> : undefined}
                              sx={{ fontSize: '0.8125rem' }}
                            >
                              {isOAuth ? `Connect with ${conn.name}` : 'Set up'}
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}

      {/* Confirmation dialog */}
      <Dialog open={confirmationAction.open} onClose={() => setConfirmationAction((prev) => ({ ...prev, open: false }))}>
        <DialogTitle>
          {confirmationAction.type === 'reconfigure' ? 'Reconnect?' : confirmationAction.type === 'enable' ? 'Enable connector?' : 'Disable connector?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmationAction.type === 'reconfigure'
              ? `To re-enable ${confirmationAction.connector?.name}, you'll need to reconnect your account. This lets you switch to a different account if needed.`
              : confirmationAction.type === 'enable'
                ? `Enable the ${confirmationAction.connector?.name} connector?`
                : `Disable the ${confirmationAction.connector?.name} connector? You can re-enable it any time.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationAction((prev) => ({ ...prev, open: false }))}>Cancel</Button>
          <Button onClick={handleConfirmToggle} color={confirmationAction.type === 'disable' ? 'error' : 'primary'} variant="contained">
            {confirmationAction.type === 'reconfigure' ? 'Reconnect' : confirmationAction.type === 'enable' ? 'Enable' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConnectorSetupModal
        open={isConnectorModalOpen}
        connector={currentConnector}
        inputs={connectorInputs}
        setInputs={setConnectorInputs}
        isLoading={isConnectorLoading}
        error={modalError}
        setError={setModalError}
        isExisting={!!userConnectorAuth[currentConnector?.id]?._id}
        onClose={() => setIsConnectorModalOpen(false)}
        onAuthorize={handleAuthorizeConnector}
      />
    </Box>
  );
};

export default IntegrationsManager;
